import {
  type CreateParticipantQrBehaviourRequest,
  isBehaviourDisabled,
  type ParticipantBehaviour,
} from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, NumberedBadge } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { FormStack } from '../../components/forms/FormStack';
import { HookedMethodFormField } from '../../components/forms/HookedMethodFormField';

interface Props {
  participantBehaviours: ParticipantBehaviour[];
}

export const ParticipantQRBehavioursControl = (props: Props) => {
  const { participantBehaviours } = props;

  const methodCounts = (
    useWatch({ name: 'behaviours' }) as CreateParticipantQrBehaviourRequest[]
  ).reduce<Record<string, number>>((acc, behaviour) => {
    acc[behaviour.behaviourId] = Object.values(behaviour.methods).filter(
      (method) => method.enabled,
    ).length;
    return acc;
  }, {});

  if (P.isEmpty(participantBehaviours)) {
    return <Empty title='No behaviours' description='This participant has no behaviours' py={0} />;
  }

  return (
    <ChakraV3.Accordion.Root multiple collapsible>
      {participantBehaviours.map((behaviour, index) => {
        const behaviourDisabled = isBehaviourDisabled(behaviour.status);
        const methodCount = methodCounts[behaviour.id] ?? 0;

        return (
          <ChakraV3.Accordion.Item
            value={behaviour.id}
            key={behaviour.id}
            disabled={behaviourDisabled}
          >
            <ChakraV3.Accordion.ItemTrigger px={4} bg='gray.50'>
              <ChakraV3.HStack w='full'>
                <ChakraV3.Text fontSize='sm'>{behaviour.name}</ChakraV3.Text>
                <ChakraV3.Spacer />
                <NumberedBadge count={methodCount} />
                <ChakraV3.Accordion.ItemIndicator />
              </ChakraV3.HStack>
            </ChakraV3.Accordion.ItemTrigger>
            <ChakraV3.Accordion.ItemContent px={4} py={3}>
              <FormStack>
                <HookedMethodFormField
                  name={`behaviours.[${index}].methods.abc`}
                  label='ABC'
                  helperText='Antecedent, behaviour, consequence data'
                  border={0}
                  px={2}
                  py={0}
                  labelSize='sm'
                />
                <HookedMethodFormField
                  name={`behaviours.[${index}].methods.frequency`}
                  label='Frequency'
                  helperText='Number of times a behaviour occurs'
                  border={0}
                  px={2}
                  py={0}
                  labelSize='sm'
                />
                <HookedMethodFormField
                  name={`behaviours.[${index}].methods.duration`}
                  label='Duration'
                  helperText='How long a behaviour occurs for'
                  border={0}
                  px={2}
                  py={0}
                  labelSize='sm'
                />
                <HookedMethodFormField
                  name={`behaviours.[${index}].methods.episodicSeverity`}
                  label='Episodic Severity'
                  helperText='How severe a behaviour is'
                  border={0}
                  px={2}
                  py={0}
                  labelSize='sm'
                />
              </FormStack>
            </ChakraV3.Accordion.ItemContent>
          </ChakraV3.Accordion.Item>
        );
      })}
    </ChakraV3.Accordion.Root>
  );
};
